import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, InputAdornment, Box, Typography } from '@mui/material';
import { useLoadScript } from '@react-google-maps/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HistoryIcon from '@mui/icons-material/History';
import { debounce } from 'lodash';

// Need to ensure lodash is installed, or implement simple debounce.
// Let's implement a simple debounce to avoid adding a dependency if not already there,
// but MUI usually has it or we can just write one.

const LIBRARIES: any[] = ['places'];

export interface LocationData {
  id: string; // Google Place ID or internal UUID
  name: string; // City Name
  province: string; // State/Province/Country
  latitude?: number;
  longitude?: number;
}

interface LocationAutocompleteProps {
  label: string;
  placeholder: string;
  icon: React.ReactNode;
  value: LocationData | null;
  onChange: (location: LocationData | null) => void;
  modeColor?: string; // e.g., '#2563eb' for driver, '#059669' for passenger
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  label,
  placeholder,
  icon,
  value,
  onChange,
  modeColor = '#2563eb'
}) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [recentCities, setRecentCities] = useState<LocationData[]>([]);

  interface PlaceType {
    description: string;
    place_id: string;
    structured_formatting: {
      main_text: string;
      secondary_text: string;
    };
    isRecent?: boolean;
  }

  // Load recent cities on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('recent_cities');
      if (stored) {
        setRecentCities(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Error reading recent cities:', e);
    }
  }, []);

  const saveToRecent = (city: LocationData) => {
    try {
      const stored = localStorage.getItem('recent_cities');
      let list: LocationData[] = stored ? JSON.parse(stored) : [];
      // Remove if already exists (move to top)
      list = list.filter(item => item.id !== city.id);
      // Add to front
      list.unshift(city);
      // Keep last 5
      if (list.length > 5) {
        list = list.slice(0, 5);
      }
      localStorage.setItem('recent_cities', JSON.stringify(list));
      setRecentCities(list);
    } catch (e) {
      console.error('Error saving recent city:', e);
    }
  };

  const fetchWrapper = useMemo(
    () => {
      let timeoutId: NodeJS.Timeout;
      return (request: { input: string }, callback: (results?: readonly PlaceType[]) => void) => {
        if (!window.google) return;
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          console.log('Fetching autocomplete predictions for input:', request.input);
          autocompleteService.getPlacePredictions(
            { input: request.input }, // Removed types restriction to support airports/POIs/regions
            (results, status) => {
              console.log('Autocomplete status:', status, 'Results:', results);
              if (results) {
                callback(results);
              } else {
                callback([]);
              }
            }
          );
        }, 300);
      };
    },
    []
  );

  useEffect(() => {
    let active = true;

    if (inputValue === '') {
      const recentOptions = recentCities.map(city => ({
        description: `${city.name}, ${city.province}`,
        place_id: city.id,
        structured_formatting: { main_text: city.name, secondary_text: city.province },
        isRecent: true
      }));

      const baseOptions = value ? [{
        description: `${value.name}, ${value.province}`,
        place_id: value.id,
        structured_formatting: { main_text: value.name, secondary_text: value.province },
        isRecent: false
      }] : [];

      const merged = [...baseOptions, ...recentOptions].filter(
        (v, i, a) => a.findIndex(t => t.place_id === v.place_id) === i
      );

      setOptions(merged);
      return;
    }

    if (!isLoaded || !window.google) {
      return;
    }

    fetchWrapper({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [{
            description: `${value.name}, ${value.province}`,
            place_id: value.id,
            structured_formatting: { main_text: value.name, secondary_text: value.province },
            isRecent: false
          }];
        }

        if (results) {
          newOptions = [...newOptions, ...results.map(r => ({ ...r, isRecent: false }))];
        }

        // Deduplicate
        const uniqueOptions = newOptions.filter((v, i, a) => a.findIndex(t => (t.place_id === v.place_id)) === i);
        setOptions(uniqueOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetchWrapper, isLoaded, recentCities]);

  const handleSelect = async (event: any, newValue: PlaceType | null) => {
    if (!newValue) {
      onChange(null);
      return;
    }

    if (!window.google) return;

    // Use Geocoder to get Lat/Lng
    const geocoder = new window.google.maps.Geocoder();
    try {
      const result = await geocoder.geocode({ placeId: newValue.place_id });
      if (result.results && result.results.length > 0) {
        const { lat, lng } = result.results[0].geometry.location;
        const locationData: LocationData = {
          id: newValue.place_id,
          name: newValue.structured_formatting.main_text,
          province: newValue.structured_formatting.secondary_text,
          latitude: lat(),
          longitude: lng(),
        };
        onChange(locationData);
        saveToRecent(locationData);
      } else {
        const locationData = {
          id: newValue.place_id,
          name: newValue.structured_formatting.main_text,
          province: newValue.structured_formatting.secondary_text,
        };
        onChange(locationData);
        saveToRecent(locationData);
      }
    } catch (e) {
      console.error('Geocoding error:', e);
      const locationData = {
        id: newValue.place_id,
        name: newValue.structured_formatting.main_text,
        province: newValue.structured_formatting.secondary_text,
      };
      onChange(locationData);
      saveToRecent(locationData);
    }
  };

  return (
    <Autocomplete
      fullWidth
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        if (option && option.structured_formatting && option.structured_formatting.main_text) {
          return option.structured_formatting.main_text;
        }
        return option?.description || '';
      }}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value ? {
        description: `${value.name}, ${value.province}`,
        place_id: value.id,
        structured_formatting: { main_text: value.name, secondary_text: value.province }
      } : null}
      onChange={handleSelect}
      onInputChange={(event, newInputValue) => {
        console.log('User typed new input:', newInputValue);
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                {icon}
              </InputAdornment>
            ),
            sx: { fontSize: '1.5rem' },
          }}
          InputLabelProps={{
            sx: { fontSize: '1.5rem' },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '& input': {
                py: 2,
                fontSize: '1.5rem',
              },
              '&:hover fieldset': {
                borderColor: modeColor,
              },
              '&.Mui-focused fieldset': {
                borderColor: modeColor,
              },
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        console.log('Rendering autocomplete option in dropdown:', option.place_id, option.description);
        return (
          <li {...props} key={option.place_id}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {option.isRecent ? (
                <HistoryIcon sx={{ color: 'text.secondary', mr: 2, fontSize: '1.5rem' }} />
              ) : (
                <LocationOnIcon sx={{ color: 'text.secondary', mr: 2 }} />
              )}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                {option.structured_formatting.main_text}
                {option.isRecent && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', bgcolor: '#f3f4f6', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>
                    Recent
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                {option.structured_formatting.secondary_text}
              </Typography>
            </Box>
          </Box>
        </li>
      ); }}
    />
  );
};

export default LocationAutocomplete;
